import {
    addMainControls,
    addOverlayControls,
    WithMainControlsProps,
    WithOverlayControlsProps,
} from '@eventbrite/eds-structure';
import { sdkRequest } from '@eventbrite/http';
import { setLanguage } from '@eventbrite/i18n';
import { addDimensions, trackAnalyticsEvent } from '@eventbrite/site-analytics';
import React, { useEffect, useState } from 'react';
import BoostBannerRoot from './components/Banner/BoostBannerRoot';
import BookmarkRoot from './components/Bookmark/BookmarkRoot';
import CreatorCollectionsRoot from './components/CreatorCollections/CreatorCollectionsRoot';
import EventMapRoot from './components/EventMap/EventMapRoot';
import { ExpiredEvents } from './components/ExpiredEvents/ExpiredEvents';
import { FollowOrganizerController } from './components/FollowOrganizer/FollowOrganizerController';
import NotificationRoot from './components/Notification/NotificationRoot';
import { NotificationBar } from './components/NotificationBar/NotificationBar';
import OverlayRoot from './components/Overlay/OverlayRoot';
import RelatedEventsRoot from './components/RelatedEvents/RelatedEventsRoot';
import { ReportThisEventRoot } from './components/ReportThisEvent/ReportThisEventRoot';
import RSVPRoot from './components/RSVP/RSVPRoot';
import { SearchTakeoverRoot } from './components/SearchTakeover/SearchTakeoverRoot';
import SeriesDatesDropdownRoot from './components/SeriesDatesDropdown/SeriesDatesDropdownRoot';
import ShareEventRoot from './components/ShareEvent/ShareEventRoot';
import { StickyNav } from './components/StickyNav/StickyNav';
import { Analytics } from './components/utils/analytics';
import { useLocalizedDates } from './components/utils/localizedDates';
import { OrganizerContext, SettingsContext, UserContext } from './context';
import { NotificationsContextProvider } from './contexts';
import {
    AppProps,
    CollectionResponse,
    ControlledProps,
    EnhancedAppProps,
    EventCollectionsResponse,
} from './types';

const setUpDimensions = (props: AppProps): void => {
    const {
        app_name: appName,
        app_version: appVersion,
        event,
        gaSettings,
    } = props;
    const {
        activeScopeUserId,
        correlationId,
        guestPartnerId,
        userPartnerId: userId,
        guestPartnerId: guestId,
    } = gaSettings;
    const {
        id: eventId,
        ownerId: eventOwnerUserId,
        source: eventSource,
    } = event;

    const dimensions = {
        activeScopeUserId,
        appName,
        appVersion,
        correlationId,
        eventId,
        eventOwnerUserId,
        eventSource,
        experimentId: 'OldListing',
        guestId,
        guestPartnerId,
        userId,
    };

    addDimensions(dimensions);
};

const ControlledListingComponents = (
    props: ControlledProps & WithMainControlsProps & WithOverlayControlsProps,
) => {
    return (
        <>
            <NotificationRoot {...props} />
            <RelatedEventsRoot {...props} />
            <OverlayRoot {...props} />
        </>
    );
};

const EnhancedListingComponents = addOverlayControls<EnhancedAppProps>(
    addMainControls(ControlledListingComponents),
);

const useCreatorCollections = (
    eventId: string,
    launchLinkToCreatorCollectionsOnListing: boolean,
) => {
    const [collections, setCollections] = useState<
        CollectionResponse[] | undefined
    >(undefined);

    const isExpiredEvent = document.getElementsByClassName(
        'js-expired-related-events',
    );

    useEffect(() => {
        let isMounted = true;

        if (isExpiredEvent.length > 0) {
            return;
        }

        if (launchLinkToCreatorCollectionsOnListing) {
            sdkRequest(`/events/${eventId}/collections/public/`).then(
                (storedCollections: EventCollectionsResponse) => {
                    if (storedCollections && isMounted)
                        return setCollections(storedCollections.collections);
                },
            );
        } else {
            return setCollections([]);
        }
        return () => {
            isMounted = false;
        };
    }, [eventId, launchLinkToCreatorCollectionsOnListing, isExpiredEvent]);

    return collections;
};

export const App: React.FC<AppProps> = (props: AppProps) => {
    const {
        env: {
            localeInfo: { locale },
        },
        user: { isAuthenticated, id: userId },
        event: {
            childEvents,
            end,
            hideEndDate,
            hideStartDate,
            id: eventId,
            isOnlineEvent,
            isSeriesParent,
            name: eventName,
            start,
            url: eventUrl,
            isExpiredEvent = false,
        },
        settings: {
            featureFlags: {
                launchLinkToCreatorCollectionsOnListing = false,
                launchStickyNavigationOnListings = false,
                launchExpiredEventsWithoutShareAndBookmarkFeatures = false,
                launchLocalizedDatesOnListings = false,
                launchAnalyticsOnListings = false,
            } = {},
        },
        gaSettings,
        tracking,
        components: {
            remindMe: { isRemindedEvent = false },
            bookmark: { isBookmarkedEvent = false } = {},
            followOrganizer: { isGDPRCountry },
            contactOrganizer: { captchaKey },
        },
        organizer: {
            id: organizerId,
            name: organizerName,
            url: organizerUrl,
            profilePicture: organizerProfilePicture,
            description: organizerDescription,
            eventTitle: organizerEventTitle,
            orgFacebook: organizerFacebook,
            orgTwitter: organizerTwitter,
            orgWebsite: organizerWebsite,
        },
    } = props;

    setLanguage(locale);

    setUpDimensions(props);
    trackAnalyticsEvent({
        action: 'AppLoad',
        category: 'listing',
    });
    const modalContainer = React.useRef<HTMLDivElement | null>(null);
    const creatorCollections = useCreatorCollections(
        eventId,
        launchLinkToCreatorCollectionsOnListing,
    );

    if (launchStickyNavigationOnListings) {
        StickyNav();
    }

    if (launchExpiredEventsWithoutShareAndBookmarkFeatures && isExpiredEvent) {
        ExpiredEvents();
    }

    if (launchAnalyticsOnListings) {
        Analytics({ ...tracking, eventId });
    }

    useLocalizedDates(
        {
            end,
            hideEndDate,
            hideStartDate,
            isOnlineEvent,
            isSeriesParent,
            locale,
            start,
        },
        launchLocalizedDatesOnListings,
    );

    return (
        <div data-spec="listing-page-app">
            <SettingsContext.Provider value={props.settings}>
                <UserContext.Provider value={props.user}>
                    <OrganizerContext.Provider value={props.organizer}>
                        <ShareEventRoot
                            {...props}
                            modalContainer={modalContainer}
                        />
                        <NotificationsContextProvider>
                            <FollowOrganizerController
                                orgFacebook={organizerFacebook}
                                orgTwitter={organizerTwitter}
                                orgWebsite={organizerWebsite}
                                organizerId={organizerId}
                                organizerName={organizerName}
                                organizerProfilePicture={
                                    organizerProfilePicture
                                }
                                organizerUrl={organizerUrl}
                                organizerDescription={organizerDescription}
                                organizerEventTitle={organizerEventTitle}
                                organizers={[organizerId]}
                                organizerData={{
                                    [organizerId]: {
                                        id: organizerId,
                                        name: organizerName,
                                        url: organizerUrl,
                                        profilePicture: organizerProfilePicture,
                                        followedByYou: false,
                                    },
                                }}
                                userId={userId && userId.toString()}
                                captchaKey={captchaKey}
                                isAuthenticated={isAuthenticated}
                                eventName={eventName}
                                eventId={eventId}
                                eventUrl={eventUrl}
                                isRemindedEvent={isRemindedEvent}
                                isGDPRCountry={isGDPRCountry}
                                gaSettings={gaSettings}
                                gaCategory={gaSettings.category}
                            />
                            <NotificationBar />
                            <BookmarkRoot
                                eventId={eventId}
                                isBookmarked={isBookmarkedEvent}
                                queryParams={location.search}
                            />
                        </NotificationsContextProvider>
                        <RSVPRoot eventId={eventId} />
                        <SeriesDatesDropdownRoot
                            childEvents={childEvents}
                            eventId={eventId}
                            isOnlineEvent={isOnlineEvent}
                        />
                        <ReportThisEventRoot {...props} />
                        <SearchTakeoverRoot {...props} />
                        <CreatorCollectionsRoot {...props} />
                        <EnhancedListingComponents
                            {...props}
                            creatorCollections={creatorCollections}
                        />
                        <div
                            id="share-modal-wrapper"
                            ref={modalContainer}
                        ></div>
                        <EventMapRoot {...props} />
                        <BoostBannerRoot eventId={eventId} />
                    </OrganizerContext.Provider>
                </UserContext.Provider>
            </SettingsContext.Provider>
        </div>
    );
};
